import useUserProfile from '@amzn/PassportUserManagementUIAssets/dist/hooks/useUserProfile';
import { BUTTON_CLICK } from '@amzn/carrierlogistics-commons/dist/constants/analytics';
import { DEVICE_WIDTHS } from '@amzn/carrierlogistics-commons/dist/constants/breakpoints';
import {
  useLpAccessRights,
  useSelectedLp,
} from '@amzn/carrierlogistics-commons/dist/hooks/authorisation';
import {
  recordCustomEventInCloudWatchRUM,
  setCustomSessionProperties,
} from '@amzn/carrierlogistics-commons/dist/utils/cloudwatchRum';
import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Masthead, {
  MastheadMenuButton,
  MastheadTitle,
} from '@amzn/meridian/masthead';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row';
import SideMenu, {
  SideMenuLink,
  SideMenuTitle,
} from '@amzn/meridian/side-menu';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import NavigationButtons from '../components/NavigationButtons';
import LpDropdown from '../components/molecules/LpDropdown';
import ManageUsersButton from '../components/molecules/ManageUsersButton';
import {
  FAQ_PAGE_PATH,
  GET_STARTED_PATH,
  HOME,
  MANAGE_USERS_PAGE,
  PROFILE_PAGE,
} from '../config/navigationalRoutes';
import {
  HEADER_FAQ_BUTTON,
  HEADER_GET_STARTED_BUTTON,
  MANAGE_USERS_CLICK,
  MY_PROFILE_CLICK,
  SETTINGS_BUTTON_CLICK,
} from '../constants/analytics';
import { INTERNAL_USER_IDENTIFIER } from "../constants/common";
import useNavigateToSpecifiedRoute from '../hooks/useNavigateToSpecifiedRoute';
import useUserAccountActionRedirects from '../hooks/useUserAccountActionRedirects';
import logo from '../images/carrier-hub-logo.svg';
import CarrierLogisticsLogo from '../images/cl-logo.svg';
import getSettings from '../utils/auth/getSettings';
import { isANonSignedInPage, isAuthorizedPage } from '../utils/authorizedPageUtils';
import FeedbackModule from './FeedbackModule';
import InlineHelpModule from './InlineHelpModule';
import LanguageToggleModule from './LanguageToggleModule';

const menubarLinkCss = css`
  & {
    font-weight: 700;
    font-size: 14px;
  }
  &:hover {
    color: #4db9b0;
  }
`;
const getStartedHoverCss = css`
  &:hover {
    color: #232f3e;
  }
`;

const TopBar = ({ onClick }) => {
  const { pathname } = useLocation();
  const { hasAdminRights } = useLpAccessRights();
  const selectedLp = useSelectedLp();
  const {
    lpId, logisticsPartyDisplayName, logisticsPartyName, countryCode,
  } = selectedLp || {};
  const { userIdentifier, email } = useUserProfile();
  const isInternalUser = email && email.endsWith(INTERNAL_USER_IDENTIFIER);
  const { customerId } = userIdentifier || {};

  // to render menu on mobile screen
  const [showSideBar, setShowSidebar] = useState(false);
  const isNonSignedInPage = isANonSignedInPage(pathname);
  const { signInRedirect, signOutRedirect } = useUserAccountActionRedirects();
  const { navigateToSpecifiedRoute } = useNavigateToSpecifiedRoute();
  const { formatMessage } = useIntl();

  const handleSignOutClick = useCallback(() => {
    signOutRedirect();
    getSettings().then(({ rumUIParams }) => {
      setCustomSessionProperties({}, rumUIParams);
    });
  }, [signOutRedirect]);

  const onFAQClick = useCallback(() => {
    setShowSidebar(false);
    recordCustomEventInCloudWatchRUM(BUTTON_CLICK, {
      action_properties: {
        name: HEADER_FAQ_BUTTON,
      },
    });
    navigateToSpecifiedRoute(FAQ_PAGE_PATH);
  }, [navigateToSpecifiedRoute]);
  const onGetStartedClick = useCallback(() => {
    setShowSidebar(false);
    recordCustomEventInCloudWatchRUM(BUTTON_CLICK, {
      action_properties: {
        name: HEADER_GET_STARTED_BUTTON,
      },
    });
    navigateToSpecifiedRoute(GET_STARTED_PATH);
  }, [navigateToSpecifiedRoute]);
  const goToProfilePage = useCallback(() => {
    recordCustomEventInCloudWatchRUM(BUTTON_CLICK, {
      action_properties: {
        name: MY_PROFILE_CLICK,
      },
    });
    navigateToSpecifiedRoute(PROFILE_PAGE);
  }, [navigateToSpecifiedRoute]);
  const logoClickHandler = () => {
    navigateToSpecifiedRoute(HOME);
  };
  const goToManageUsersPage = useCallback(() => {
    recordCustomEventInCloudWatchRUM(BUTTON_CLICK, {
      action_properties: {
        name: MANAGE_USERS_CLICK,
      },
    });
    navigateToSpecifiedRoute(MANAGE_USERS_PAGE);
  }, [navigateToSpecifiedRoute]);
  const buttonRef = useRef();
  const [open, setOpen] = useState(false);
  const onSettingsClick = useCallback(() => {
    setOpen(true);
    recordCustomEventInCloudWatchRUM(BUTTON_CLICK, {
      action_properties: {
        name: SETTINGS_BUTTON_CLICK,
      },
    });
  }, []);
  const onSettingsClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const customSessionProperties = {
      ...(lpId ? { logisticsPartyId: lpId } : {}),
      ...(logisticsPartyDisplayName ? { logisticsPartyDisplayName } : {}),
      ...(logisticsPartyName ? { logisticsPartyName } : {}),
      ...(customerId ? { customerId } : {}),
      ...(countryCode ? { countryCode } : {}),
      isInternalUser: isInternalUser || false,
    };
    if (Object.keys(customSessionProperties).length) {
      getSettings().then(({ rumUIParams }) => {
        setCustomSessionProperties(customSessionProperties, rumUIParams);
      });
    }
  }, [
    lpId,
    logisticsPartyDisplayName,
    logisticsPartyName,
    customerId,
    countryCode,
  ]);

  return (
    <Responsive
      query="min-width"
      props={{
        isSmallScreen: {
          default: true,
          [DEVICE_WIDTHS.DESKTOP]: false,
        },
        hideMenu: {
          default: false,
          [DEVICE_WIDTHS.DESKTOP]: true,
        },
      }}
    >
      {({ isSmallScreen, hideMenu }) => (
        <div className="home-nav-header">
          <Masthead size="large">
            {!isNonSignedInPage && <MastheadMenuButton onClick={onClick} />}
            <MastheadTitle>
              <Row wrap="down">
                <img
                  src={logo}
                  width={195}
                  alt="Logo"
                  onClick={logoClickHandler}
                />
                {!isSmallScreen && (
                  <span selected={false}>
                    {isAuthorizedPage(pathname) && <LpDropdown />}
                  </span>
                )}
              </Row>
            </MastheadTitle>
            {isNonSignedInPage ? (
              <>
                {hideMenu ? (
                  <>
                    <NavigationButtons
                      type="link"
                      onClick={onFAQClick}
                      className={menubarLinkCss}
                      text="top_bar.frequently_asked_questions_button"
                      color="inverted"
                    />
                    <NavigationButtons
                      type="link"
                      onClick={signInRedirect}
                      className={menubarLinkCss}
                      text="top_bar.sign_in_button"
                      color="inverted"
                    />
                    <LanguageToggleModule />
                    <NavigationButtons
                      type="secondary"
                      onClick={onGetStartedClick}
                      className={`${menubarLinkCss} ${getStartedHoverCss} `}
                      text="top_bar.get_started_button"
                    />
                  </>
                ) : (
                  <MastheadMenuButton
                    onClick={setShowSidebar}
                    data-testid="homescreen-menu"
                  />
                )}
                {/* // Menu Items on the sidebar for smaller screens */}
                <Row alignmentVertical="stretch" spacing="none">
                  <SideMenu
                    open={showSideBar}
                    onClose={() => setShowSidebar(false)}
                    type="overlay"
                  >
                    <SideMenuTitle>
                      <img src={CarrierLogisticsLogo} alt="" />
                    </SideMenuTitle>

                    <SideMenuLink
                      selected={pathname.includes(FAQ_PAGE_PATH)}
                      onClick={onFAQClick}
                    >
                      <FormattedMessage id="top_bar.frequently_asked_questions_button" />
                    </SideMenuLink>
                    <SideMenuLink onClick={signInRedirect}>
                      <FormattedMessage id="top_bar.sign_in_button" />
                    </SideMenuLink>
                    <LanguageToggleModule />
                    <SideMenuLink
                      selected={pathname.includes(GET_STARTED_PATH)}
                      onClick={onGetStartedClick}
                    >
                      <FormattedMessage id="top_bar.get_started_button" />
                    </SideMenuLink>
                  </SideMenu>
                </Row>
              </>
            ) : (
              <>
                {!isSmallScreen && (
                  <>
                    <LanguageToggleModule />
                    <FeedbackModule />
                    <InlineHelpModule />
                  </>
                )}
                <Button
                  ref={buttonRef}
                  onClick={onSettingsClick}
                  type="icon"
                  size="small"
                  label="settings"
                  aria-controls="my-menu"
                  aria-haspopup="menu"
                  aria-expanded="false"
                >
                  <Icon tokens={cogTokens} />
                </Button>
                <Menu
                  anchorNode={buttonRef.current}
                  id="my-menu"
                  open={open}
                  position="left"
                  onClose={onSettingsClose}
                >
                  <MenuItem onClick={goToProfilePage}>
                    <FormattedMessage id="top_bar.settings_menu.my_profile" />
                  </MenuItem>
                  <MenuItem disabled>
                    <FormattedMessage id="top_bar.settings_menu.accounts" />
                  </MenuItem>
                  <MenuItem
                    onClick={goToManageUsersPage}
                    disabled={!hasAdminRights}
                  >
                    <ManageUsersButton hasAdminRights={hasAdminRights} />
                  </MenuItem>
                  <MenuItem onClick={handleSignOutClick}>
                    <FormattedMessage id="top_bar.settings_menu.sign_out" />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Masthead>
        </div>
      )}
    </Responsive>
  );
};

TopBar.propTypes = {
  onClick: PropTypes.func.isRequired,
};

export default TopBar;
